import mixpanel from "mixpanel-browser";
import { useCallback } from "react";
import ReactGA from "react-ga4";
import { isGoogleAnalyticsAllowed, shouldTrack } from "@/utils/analytics";
import { useAnalyticValues } from "./AnalyticValuesProvider";

interface StonlyWidget {
	(command: "addEventListener", eventName: string, handler: () => void): void;
	(command: "removeEventListener", eventName: string, handler: () => void): void;
	(command: "cleanup"): void;
	(command: "clearSentData"): void;
	(command: "identify", userId: string, properties?: { [key: string]: string | number | boolean | Date }): void;
	(command: "sendData", data: unknown): void;
	(command: "track", eventName: string): void;
}
declare global {
	interface Window {
		StonlyWidget?: StonlyWidget;
	}
}

export type AnalyticValues = Record<string, string | number | boolean | null | undefined>;

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN;
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
const ENV = import.meta.env.VITE_ENVIROMENT || "development";
const SEVERITY_KEY = "__severity";

type TrackFn = (event: string, props?: AnalyticValues) => void;
type AnalyticUtils = {
	track: TrackFn;
	trackError: TrackFn;
	/** `adminRoles` is the user's roles in the chosen department (empty when none are set). */
	setUser: (_: { departmentId: string; userId: string; role: string; firstName: string; adminRoles: string[] }) => void;
};

mixpanel.init(MIXPANEL_TOKEN, {
	debug: false,
	track_pageview: true,
	persistence: "localStorage",
	record_sessions_percent: 0,
	record_heatmap_data: true,
	record_mask_text_selector: "input[type='password'], input[name='password']",
	record_block_selector: "input[type='password'], input[name='password']",
});
mixpanel.register({ env: ENV });
// Off on the public careers page (`isGoogleAnalyticsAllowed`): without `initialize`, ReactGA loads no script and sends nothing.
if (isGoogleAnalyticsAllowed()) ReactGA.initialize(GA_MEASUREMENT_ID);

export function useAnalytics(): AnalyticUtils {
	const analyticValues = useAnalyticValues();

	const track = useCallback<AnalyticUtils["track"]>(
		(title, properties) => {
			if (!shouldTrack()) {
				return;
			}

			const allProps = { ...analyticValues, ...properties, [SEVERITY_KEY]: "info", __v: 2, env: ENV };

			mixpanel.track(title, allProps);
			ReactGA.event(title, allProps);

			window.StonlyWidget?.("track", title);
			window.StonlyWidget?.("sendData", { [title]: Object.fromEntries(Object.entries(allProps).map(([key, value]) => [key.replaceAll("_", "-"), value])) });
		},
		[analyticValues],
	);
	const trackError = useCallback<AnalyticUtils["trackError"]>(
		(title, properties) => {
			if (!shouldTrack()) {
				return;
			}

			const allProps = { ...analyticValues, ...properties, [SEVERITY_KEY]: "error", __v: 2, env: ENV };

			mixpanel.track(title, allProps);
			ReactGA.event(title, allProps);

			window.StonlyWidget?.("track", title);
			window.StonlyWidget?.("sendData", { [title]: Object.fromEntries(Object.entries(allProps).map(([key, value]) => [key.replaceAll("_", "-"), value])) });
		},
		[analyticValues],
	);
	const setUser = useCallback<AnalyticUtils["setUser"]>(({ departmentId, userId, role, firstName, adminRoles }) => {
		const userProps = { department_id: departmentId, user_role: role, first_name: firstName };
		// Mixpanel keeps a list profile property as a list, so each role stays filterable on its own.
		mixpanel.people.set({ ...userProps, admin_roles: adminRoles });
		// GA event parameters are scalars — a list would be dropped, so it goes over as joined text.
		ReactGA.event("setUserProps", { ...userProps, admin_roles: adminRoles.join(",") });

		window.StonlyWidget?.("identify", `userId:${userId}@deptId:${departmentId}`, { "user-id": userId, "department-id": departmentId, env: ENV });
	}, []);

	return { track, trackError, setUser };
}
